import { IconChevronLeft, PageHeaderIconLink } from 'cdk-radial';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Tabs, VIRTUAL_CARD } from 'common/constants';
import PageHeaderTitleWithButtons from 'components/PageHeader/PageHeaderTitleWithButtons';
import { useFlags } from 'hooks';
import { calculateTotalNetAmountSum } from 'pages/AuthorizePaymentPage/helpers';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { updateActiveAuthorizePaymentPage } from 'pages/VendorPaymentResultPage/slice';
import {
  getSelectedVendors,
  setInterCompanyCIBAccount,
  setSelectedVendors
} from 'pages/VendorsTablePage/components/PrintChecks';
import { getPaymentMethods } from 'pages/VendorsTablePage/components/VendorsTable/slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  StyledPageHeaderWrapper,
  StyledPageHeaderTitle,
  StyledPageHeaderWrapperForAPClerk,
  TotalSelectedChecks,
  TotalSelectedChecksContainer
} from '../../styled';
import { AuthorizePaymentHeaderProps } from '../../types';

export const AuthorizePaymentHeader = ({
  handleAuthorizePayment,
  disableAuthButton,
  isLoading
}: AuthorizePaymentHeaderProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const paymentMethods = useAppSelector(getPaymentMethods);
  const selectedTab = useAppSelector(getSelectedTab);
  const selectedPrintChecks = useAppSelector(getSelectedVendors);
  const flags = useFlags();
  const goBack = (): void => {
    dispatch(updateActiveAuthorizePaymentPage(''));
    dispatch(setSelectedVendors([]));
    dispatch(
      setInterCompanyCIBAccount({
        companyId: '',
        accountName: '',
        accountNumber: ''
      })
    );
  };

  const pageHeaderTitle = useMemo(() => {
    switch (selectedTab) {
      case Tabs.ToBePaid:
        return (
          <FormattedMessage
            defaultMessage="Request Payment Authorization"
            description="Request Payment Authorization page header"
          />
        );
      case Tabs.NeedsAuthorization:
        return (
          <FormattedMessage
            defaultMessage="Authorize Payment"
            description="Authorize Payment page header"
          />
        );
      case Tabs.PrintChecks:
        return (
          <FormattedMessage
            defaultMessage="Review Checks"
            description="Review Checks page header"
          />
        );
      default:
        return null;
    }
  }, [selectedTab]);

  const actionButtonText = useMemo(() => {
    switch (selectedTab) {
      case Tabs.ToBePaid:
        return (
          <FormattedMessage
            defaultMessage="Request Authorization"
            description="Request Authorization Button to submit the information"
          />
        );
      case Tabs.NeedsAuthorization:
        return (
          <FormattedMessage
            defaultMessage="Authorize"
            description="Authorize Button to submit the information"
          />
        );
      case Tabs.PrintChecks:
        return (
          <FormattedMessage
            defaultMessage="Print Checks"
            description="Print Checks to submit the information"
          />
        );
      default:
        return null;
    }
  }, [selectedTab]);

  return (
    <StyledPageHeaderWrapper data-testid="page-header">
      {disableAuthButton ? (
        <StyledPageHeaderWrapperForAPClerk>
          <PageHeaderIconLink
            href="#"
            icon={
              <IconChevronLeft
                focusable={false}
                height={24}
                role="presentation"
              />
            }
            data-testid="btn-back-authorize"
            onClick={goBack}
            text=""
          />
          <StyledPageHeaderTitle>{pageHeaderTitle}</StyledPageHeaderTitle>
        </StyledPageHeaderWrapperForAPClerk>
      ) : (
        <>
          <PageHeaderIconLink
            data-testid="btn-back-authorize"
            href="#"
            icon={
              <IconChevronLeft
                focusable={false}
                height={24}
                role="presentation"
              />
            }
            onClick={goBack}
            text=""
          />
          <StyledPageHeaderTitle>{pageHeaderTitle}</StyledPageHeaderTitle>
          <PageHeaderTitleWithButtons
            buttons={{
              primary:
                flags.dmsAccountingApPaymentCheckWriting ||
                !paymentMethods?.length ||
                paymentMethods?.includes(VIRTUAL_CARD)
                  ? {
                      text: actionButtonText,
                      onClick: handleAuthorizePayment,
                      isLoading
                    }
                  : {
                      id: '',
                      onClick: () => {},
                      text: '',
                      style: { display: 'none' }
                    }
            }}
            data-testid="page-header-title-with-buttons"
          >
            <StyledPageHeaderTitle>{pageHeaderTitle}</StyledPageHeaderTitle>
            {selectedTab === Tabs.PrintChecks && (
              <TotalSelectedChecksContainer>
                <TotalSelectedChecks id="sum-of-print-check-price">
                  Total: ${calculateTotalNetAmountSum(selectedPrintChecks)}
                </TotalSelectedChecks>
              </TotalSelectedChecksContainer>
            )}
          </PageHeaderTitleWithButtons>
        </>
      )}
    </StyledPageHeaderWrapper>
  );
};
